"use client" // This is needed for client-side hooks like useState, useEffect, useRef

import { useEffect, useRef } from "react"
import "./ScopingBenefits.css"

const ScopingBenefits = () => {
  const benefits = [
    {
      iconSrc: "/wpad.webp?height=48&width=48", // Placeholder for your icon
      iconAlt: "Clear Guideline Icon",
      title: "CREATE A CLEAR GUIDELINE",
      description: "You will get a solid project scope to ensure your product reaches bigger milestones.",
    },
    {
      iconSrc: "/calci.webp?height=48&width=48", // Placeholder for your icon
      iconAlt: "Plan Costs Icon",
      title: "PLAN YOUR COSTS AHEAD",
      description: "By having an estimate from the start you can identify the main focus areas.",
    },
    {
      iconSrc: "/plan.webp?height=48&width=48", // Placeholder for your icon
      iconAlt: "Business Plan Icon",
      title: "TIGHTER BUSINESS PLAN",
      description: "With a comprehensive roadmap, your business estimates will be more reliable.",
    },
    {
      iconSrc: "/target.webp?height=48&width=48", // Placeholder for your icon
      iconAlt: "Focus Icon",
      title: "FOCUS ON WHAT MATTERS",
      description:
        "Identify from the start the key features linked to your business to know where to focus your effort.",
    },
  ]

  const headerRef = useRef(null)
  const descriptionRef = useRef(null)
  const cardRefs = useRef([]) // Use an array of refs for multiple cards

  useEffect(() => {
    console.log("ScopingBenefits component mounted. Setting up IntersectionObserver.")

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            console.log(`Element ${entry.target.className} is now visible.`)
            observer.unobserve(entry.target) // Stop observing once visible
          }
        })
      },
      { threshold: 0.1 }, // Trigger when 10% of the element is visible
    )

    // Observe header and description
    if (headerRef.current) observer.observe(headerRef.current)
    if (descriptionRef.current) observer.observe(descriptionRef.current)

    // Observe each card
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card)
    })

    return () => {
      console.log("ScopingBenefits component unmounted. Cleaning up IntersectionObserver.")
      // Clean up observers on unmount
      if (headerRef.current) observer.unobserve(headerRef.current)
      if (descriptionRef.current) observer.unobserve(descriptionRef.current)
      cardRefs.current.forEach((card) => {
        if (card) observer.unobserve(card)
      })
    }
  }, [])

  return (
    <div className="scoping-benefits-container" id="benefits">
      <div className="container">
      <div className="scoping-benefits-wrapper">
        {/* Header Section */}
        <div className="scoping-benefits-header">
          <h2 className="scoping-benefits-title animate-left" ref={headerRef}>
            ENSURE YOUR
            <br />
            PRODUCT'S SUCCESS
            <br />
            <span className="scoping-benefits-highlight">BY PLANNING AHEAD</span>
          </h2>
          <p className="scoping-benefits-description animate-left" ref={descriptionRef}>
            In the Scoping Session you will work together with our team of project managers and developers to do a
            thorough assessment of your project idea, key requirements, time planning and cost estimate.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="scoping-benefits-grid">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="scoping-benefit-card animate-up"
              ref={(el) => (cardRefs.current[index] = el)}
              style={{ "--animation-delay": `${index * 0.1}s` }} // Staggered animation
            >
              <div className="scoping-benefit-icon">
                <img src={benefit.iconSrc || "/placeholder.svg"} alt={benefit.iconAlt} className="scoping-icon-image" />
              </div>
              <h3 className="scoping-benefit-card-title">{benefit.title}</h3>
              <p className="scoping-benefit-card-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  )
}

export default ScopingBenefits
